import React, { useEffect, useState, useMemo, useCallback } from "react";
import { PatternBackground } from "../../components/PatternBackground";
import { PopupModal } from "../../components/PopupModal2";
import PDFPreviewModal from "../../components/PDFPreviewModal";
import { useLocation, useNavigate } from "react-router-dom";
import { Select as AntdSelect, Radio, Spin, Progress } from "antd";
import { serverRequest, downloadGcbDocument } from "../../util/server_request";
import { notifyUser } from "../../util/util";
import { useCompanyStatementReportPDF } from '../../templates/CompanyStatementReportTemplatePDF';
import { useMemberStatementReportPDF } from '../../templates/MemberStatementReportTemplatePDF';
import { useProfiles } from '../../hooks/api/useProfile';

const options = [
  { label: "Statement generation for members", value: "member" },
  { label: "Statement generation for company", value: "company" },
  { label: "Certificate generation for members", value: "certificate" }
];

const Checkbox = ({ checked, onChange, children }) => (
  <label style={{ 
    display: 'flex', 
    alignItems: 'center', 
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ 
        marginRight: 8,
        width: '16px',
        height: '16px',
        accentColor: '#e6ffe6',
        cursor: 'pointer'
      }}
    />
    <span>{children}</span>
  </label>
);

const StatementGeneration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { type } = location.state || {};

 const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2013 + 1 }, (_, index) => 2013 + index);
const currentDate = new Date().toISOString().split("T")[0];

  const [generationType, setGenerationType] = useState(type || "");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState("idle");
  const [apiResponse, setApiResponse] = useState(null);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [previewLoading] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubTitle, setModalSubTitle] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);

  // PDF Preview Modal states
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState(null);

  // Form state
  const [form, setForm] = useState({
    generateAll: false,
    generateYear: "",
    endDate: "",
    startDate: "",
    selectedMembers: [],
  });

  // Import both PDF generators
  const companyPdfGenerator = useCompanyStatementReportPDF();
  const memberPdfGenerator = useMemberStatementReportPDF();
  const { data: profiles } = useProfiles();
  const activeProfile = profiles?.find((p) => p.active) || profiles?.[0];
  const isGCB = activeProfile?.employerNumber === 'GCBT2/2024/0225';

  // Fetch members with pagination
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        let page = 0;
        const pageSize = 10000;
        let hasMore = true;
        const uniqueMembers = new Map();

        while (hasMore) {
          const result = await serverRequest("GET", `members?size=${pageSize}&page=${page}`);
          
          if (result.data && result.data.length > 0) {
            result.data.forEach(item => {
              // Create a more unique key by combining ssnit_number with another unique field if available
              const uniqueKey = `${item.ssnit_number}_${item.id || item.FullName || ''}`;
              
              if (!uniqueMembers.has(uniqueKey)) {
                uniqueMembers.set(uniqueKey, {
                  label: item.FullName,
                  value: item.ssnit_number,
                  originalData: item,
                  // Add a truly unique key for React
                  reactKey: uniqueKey
                });
              } else {
                console.warn(`Duplicate member detected: ${item.ssnit_number} - ${item.FullName}`);
              }
            });
            
            if (result.data.length < pageSize) {
              hasMore = false;
            } else {
              page++;
            }
          } else {
            hasMore = false;
          }
        }

        setMembers(Array.from(uniqueMembers.values()));
      } catch (error) {
        console.error("Error fetching members:", error);
        notifyUser("error", "Failed to fetch members", "Error");
      } finally {
        setLoading(false);
      }
    };

    if (generationType === "member" || generationType === "certificate") {
      fetchMembers();
    }
  }, [generationType]);

  // Generate statement/certificate function
  const generateStatement = async (type, formData, options = {}) => {
    setStatementsLoading(true);
    setGenerationStatus("generating");
    setGenerationProgress(0);

    try {
      // GCB employer: use special endpoint for member statements and certificates
      if (isGCB && (type === 'member' || type === 'certificate')) {
        const selectedMemberObjects = formData.selectedMembers
          .map(ssnit => {
            const found = members.find(m => m.value === ssnit);
            return found ? found.originalData : null;
          })
          .filter(Boolean);

        const docType = type === 'member' ? 'statement' : 'certificate';
        const total = selectedMemberObjects.length;

        for (let i = 0; i < total; i++) {
          const member = selectedMemberObjects[i];
          const gcbResult = await downloadGcbDocument(docType, member.ssnit_number || '');
          if (gcbResult?.isBlob && gcbResult.blob) {
            const url = window.URL.createObjectURL(gcbResult.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = gcbResult.filename || `${docType}_${member.ssnit_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }, 100);
          }
          setGenerationProgress(Math.floor(((i + 1) / total) * 100));
        }

        return { success: true, data: null };
      }

      let endpoint, payload;

      if (type === "company") {
        endpoint = "statements/company";
        payload = {
          specificDate: true,
          startDate: formData.startDate,
          endDate: formData.endDate,
        };
      } else if (type === "member") {
          endpoint = "statements/member";
          // Map selected SSNIT numbers to full member objects
          const selectedMemberObjects = formData.selectedMembers
            .map(ssnit => {
              // 'members' is the state array of all member objects
              const found = members.find(m => m.value === ssnit);
              return found ? found.originalData : null;
            })
            .filter(Boolean);

          payload = {
            year: parseInt(formData.generateYear, 10),
            approvalPin: "1234",
            data: selectedMemberObjects.map(member => ({
              staffNumber: "",
              ssnitNumber: member.ssnit_number || ""
            })),
            self: true
          };
      } else {
          endpoint = "statements/member/certificate";
          // Map selected SSNIT numbers to full member objects
          const selectedMemberObjects = formData.selectedMembers
            .map(ssnit => {
              const found = members.find(m => m.value === ssnit);
              return found ? found.originalData : null;
            })
            .filter(Boolean);

          payload = {
            self: true,
            data: selectedMemberObjects.map(member => ({
              staffNumber: "",
              ssnitNumber: member.ssnit_number || ""
            })),
          };
      }

      const interval = setInterval(() => {
        setGenerationProgress(prev => {
          const newProgress = prev + Math.floor(Math.random() * 10) + 1;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 500);

      const result = await serverRequest("POST", endpoint, payload);
      clearInterval(interval);
      setGenerationProgress(100);

      if (!result || result.success === false || result.code >= 400) {
        const errorMsg = result?.message || 
                        result?.error || 
                        "Request failed with no error message";
        throw new Error(errorMsg);
      }
      // Normalize API response and log it so PDF generators always receive
      // the shape they expect (items with memberData, employerData, transactionData)
      console.debug('Statement API result:', result);
      const normalizeApiResponseForPDF = (res) => {
        if (!res) return res;
        const raw = res.data;
        if (!raw) return res;
        const arr = Array.isArray(raw) ? raw : [raw];

        const normalized = arr.map(item => {
          const memberData = item.memberData || item.member || {
            memberName: item.memberName || item.fullName || item.FullName || '',
            ssnit: item.ssnit || item.ssnit_number || item.ssnitNumber || '',
            dob: item.dob || item.dateOfBirth || ''
          };

          const employerData = item.employerData || item.employer || {
            employerName: item.employerName || item.companyName || '',
            employerNumber: item.employerNumber || item.ern || ''
          };

          const transactionData = item.transactionData || item.transactions || {};

          return {
            ...item,
            memberData,
            employerData,
            transactionData
          };
        });

        return { ...res, data: normalized };
      };

      const normalizedResult = normalizeApiResponseForPDF(result);
      console.debug('Normalized statement API result for PDF generator:', normalizedResult);
      setApiResponse(normalizedResult);
      // For certificates, just return success - no PDF generation needed
      if (type === "certificate") {
        return { success: true, data: normalizedResult };
      }

      // For statements, generate PDF for preview and optionally send
      setSendingPdf(true);
      let pdfBlob;
      
      if (type === "company") {
        // Generate PDF in preview mode first
        pdfBlob = await companyPdfGenerator.generateForPreview(normalizedResult);
        
        // If autoSend is enabled, also send to server
        if (options?.autoSend) {
          await companyPdfGenerator.sendPdfToServer(pdfBlob, {
            statementType: "STATEMENT",
            metadata: {
              generationType: type,
              dateRange: `${formData.startDate} to ${formData.endDate}`
            }
          });
          
          // Auto-download the PDF after sending to server
          const url = URL.createObjectURL(pdfBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Company_Statement_${new Date().toISOString().slice(0, 10)}.pdf`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        }
      } else if (type === "member") {
        // Generate PDF in preview mode first
        pdfBlob = await memberPdfGenerator.generateForPreview(normalizedResult);
        
        // If autoSend is enabled, also send to server
        if (options?.autoSend) {
          await memberPdfGenerator.sendPdfToServer(pdfBlob, {
            statementType: "STATEMENT", 
            metadata: {
              memberCount: formData.selectedMembers?.length || 0,
              generationType: type,
              year: formData.generateYear
            }
          });
          
          // Auto-download the PDF after sending to server
          const url = URL.createObjectURL(pdfBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Member_Statement_${new Date().toISOString().slice(0, 10)}.pdf`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        }
      }

      // Store the PDF blob for preview
      setGeneratedPdfBlob(pdfBlob);
      return { success: true, data: result };

    } catch (error) {
      console.error('Generation error:', error);
      throw error;
    } finally {
      setStatementsLoading(false);
      setSendingPdf(false);
    }
  };

  // Reset generation state
  const resetGeneration = () => {
    setGenerationStatus("idle");
    setGenerationProgress(0);
    setApiResponse(null);
    setGeneratedPdfBlob(null);
  };

  // NOTE: PDF preview code was previously implemented here but the Preview button is currently commented out.
  // If preview functionality is re-enabled, restore this handler to generate and validate the PDF blob before opening the preview modal.

  // Handle PDF download from preview modal
  const handleDownloadFromPreview = useCallback(async () => {
    try {
      if (generatedPdfBlob) {
        const url = URL.createObjectURL(generatedPdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Statement_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (apiResponse) {
        // Generate and download directly if no blob exists
        if (generationType === "company") {
          await companyPdfGenerator.generateForDownload(apiResponse);
        } else if (generationType === "member") {
          await memberPdfGenerator.generateForDownload(apiResponse);
        }
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notifyUser("error", "Failed to download PDF", "Error");
    }
  }, [generatedPdfBlob, apiResponse, generationType, companyPdfGenerator, memberPdfGenerator]);

  // Main generation handler
  const handleGenerateStatement = async () => {
    // Validation
    if (generationType === "company") {
      if (form.startDate === "" || form.endDate === "") {
        notifyUser("error", "Select start date and end date to proceed!", "Validation Error");
        return;
      }
    } else if (generationType === "member") {
      if (form.selectedMembers.length < 1) {
        notifyUser("error", "Select one or more members to proceed!", "Validation Error");
        return;
      }
      if (form.generateYear === "") {
        notifyUser("error", "Select a year to proceed!", "Validation Error");
        return;
      }
    } else if (generationType === "certificate") {
      if (form.selectedMembers.length < 1) {
        notifyUser("error", "Select one or more members to proceed!", "Validation Error");
        return;
      }
    }

    try {
      const { success } = await generateStatement(generationType, form, { autoSend: true });
      
      if (success) {
        setModalTitle(generationType === "certificate" 
          ? "Certificate Generation In Progress!" 
          : "Statement Generation In Progress!");
        setModalSubTitle(
          generationType === "certificate"
            ? "Kindy Check Email Soon, Your Certificate Is Being Generated..."
            : "Kindy Check Email Soon, Your Statement Is Being Generated.."
        );
        setIsSuccess(true);
        setIsModalOpen(true);
        resetGeneration();
      }
    } catch (error) {
      console.error('Generation failed:', error);
      setModalTitle("Generation Failed");
      setModalSubTitle(error.message || "An error occurred during generation. Please try again.");
      setIsSuccess(false);
      setIsModalOpen(true);
      resetGeneration();
    }
  };

  // Modal buttons computed
  const modalButtons = useMemo(() => {
    const buttons = [{
      text: "Continue",
      className: "btn-primary",
      onClick: () => {
        setIsModalOpen(false);
        navigate("/dashboard/statements");
      }
    }];

    return buttons;
  }, [setIsModalOpen, navigate]);

  // Event handlers
  const onChange = (e) => {
    setGenerationType(e.target.value);
    resetGeneration();
  };

  const handleSelectChange = (value) => {
    setForm({
      ...form,
      selectedMembers: value,
      generateAll: value.length === members.length,
    });
  };

  const changeHandler = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const changeGenerate = (e) => {
    const checked = e.target.checked;
    setForm(prev => ({
      ...prev,
      generateAll: checked,
      selectedMembers: checked ? members.map(item => item.value) : [],
    }));
  };

  const changeStartDate = (e) => {
    setForm({ ...form, startDate: e.target.value });
  };

  const changeEndDate = (e) => {
    setForm({ ...form, endDate: e.target.value });
  };

  // Update generation type when location state changes
  useEffect(() => {
    if (type) {
      setGenerationType(type);
    }
  }, [type]);

  const isLoading = loading || statementsLoading || previewLoading;

  return (
    <div>
      <PatternBackground>
        <p className="lead fw-bold">Statement Generation</p>
        <small>Generate</small>
      </PatternBackground>
      <div className="om-container">
        <div className="p-4 shadow-sm bg-white rounded">
          <div className="mt-5">
            <p>Select one</p>
             <Radio.Group
              options={options}
              onChange={onChange}
              value={generationType}
            />
          </div>
          
          {generationStatus === 'generating' && (
            <div className="mt-4">
              <Progress 
                percent={generationProgress} 
                status="active" 
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <p className="mt-2">
                {generationType === "certificate" 
                  ? "" 
                  : ""
                }
              </p>
              {sendingPdf && (
                <p className="text-muted">
                  <small></small>
                </p>
              )}
            </div>
          )}
          
          <Spin spinning={isLoading} tip="">
            {generationType === "member" && (
               <div className="mt-5">
                <omk-select
                  label="Select Year"
                  onInput={changeHandler}
                  name="generateYear"
                  value={form.generateYear}
                  style={{ width: "auto", minWidth: "200px" }}
                >
                  <omk-select-option value="">Select Year</omk-select-option>
                  {years.map((year) => (
                    <omk-select-option key={year} value={year}>
                      {year}
                    </omk-select-option>
                  ))}
                </omk-select>
              </div>
            )}

            {(generationType === "certificate" || generationType === "member") && (
              <div className="mt-5">
                <label htmlFor="" className="form-label">Choose Members</label>
                <AntdSelect
                  size="large"
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Choose Members"
                  onChange={handleSelectChange}
                  value={form.selectedMembers}
                  options={members.map(member => ({
                    ...member,
                    key: member.value // Ensure unique key for each option
                  }))}
                  showSearch
                  loading={loading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  tagRender={(props) => {
                    const uniqueMembers = [];
                    const seenNames = new Set();
                    
                    form.selectedMembers.forEach(ssnit => {
                      const member = members.find(m => m.value === ssnit);
                      if (member && !seenNames.has(member.label)) {
                        seenNames.add(member.label);
                        uniqueMembers.push(member);
                      }
                    });

                    const currentMember = members.find(m => m.value === props.value);
                    if (!currentMember) return null;

                    const isFirstFiveUnique = uniqueMembers
                      .slice(0, 5)
                      .some(m => m.value === props.value);

                    if (isFirstFiveUnique) {
                      return (
                        <span className="ant-select-selection-item">
                          <span className="ant-select-selection-item-content">
                            {currentMember.label}
                          </span>
                          <span 
                            className="ant-select-selection-item-remove"
                            onClick={props.onClose}
                          >
                            ×
                          </span>
                        </span>
                      );
                    }

                    if (props.value === form.selectedMembers[form.selectedMembers.length - 1] && 
                        uniqueMembers.length > 5) {
                      return (
                        <span className="ant-select-selection-item">
                          <span className="ant-select-selection-item-content">
                            +{uniqueMembers.length - 5} more
                          </span>
                        </span>
                      );
                    }

                    return null;
                  }}
                />
                <div className="mt-3">
                  <Checkbox
                    onChange={changeGenerate}
                    checked={form.generateAll}
                  >
                    Generate for all members
                  </Checkbox>
                </div>
              </div>
            )}

            {generationType === "company" && (
              <div className="mt-5">
                <label htmlFor="" className="form-label">Choose range dates</label>
               <div className="row mt-2">
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <omk-date-picker
                      label="Select from date"
                      max={currentDate}
                      className="mb-4"
                      onInput={changeStartDate}
                      name="startDate"
                      value={form.startDate}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <omk-date-picker
                      label="Select end date"
                      max={currentDate}
                      className="mb-4"
                      onInput={changeEndDate}
                      name="endDate"
                      value={form.endDate}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 d-flex gap-3">
              <button
                className="btn btn-primary w-25 mobile-full-width"
                onClick={handleGenerateStatement}
                disabled={generationStatus === 'generating' || isLoading}
              >
                {generationStatus === 'generating' 
                  ? (generationType === "certificate" ? 'Generating...' : 'Generating...') 
                  : (generationType === "certificate" ? 'Generate Certificate' : 'Generate Statement')
                } 
                <i className="bi bi-arrow-right ms-2" />
              </button>
             {/* <button
                className="btn btn-outline-secondary w-25 mobile-full-width"
                onClick={handlePreviewPdf}
                disabled={(!apiResponse && !generatedPdfBlob) || previewLoading || isLoading}
                style={{ marginLeft: 8 }}
              >
                Preview
              </button>*/}
            </div>
          </Spin>
        </div>
      </div>
      
      <PopupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        subTitle={modalSubTitle}
        isSuccess={isSuccess}
        buttons={modalButtons}
      />

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={isPdfPreviewOpen}
        onClose={() => {
          setIsPdfPreviewOpen(false);
          setPdfPreviewError(null);
        }}
        pdfBlob={generatedPdfBlob}
        title={`${generationType === "company" ? "Company" : "Member"} Statement Preview`}
        onDownload={handleDownloadFromPreview}
        isLoading={previewLoading}
        error={pdfPreviewError}
      />
    </div>
  );
};

export default StatementGeneration;
